import { FormApi } from 'final-form'
import lodash from 'lodash'
import React, { useEffect, useState } from 'react'

import FormComponent from '../../../1stlog-ui/src/components/Form'
import LoaderOverlay from '../../../1stlog-ui/src/components/LoaderOverlay'
import { usePrevious } from '../../../1stlog-ui/src/hooks'
import { toast } from '../../../1stlog-ui/src/utils'
import Confirmation from './Confirmation'
import ShowSubmitValues from './Debug/ShowSubmitValues'
import Error from './Error'
import Form from './Form'
import { useProducts } from './Products'
import { TOrderEntryFormValues } from './types'
import useOrderEntry from './useOrderEntry'
import { formatOrderEntry } from './utils'

export type TOrderEntryProps = {
  onSuccess: (values) => void
  setForm: (form: FormApi) => void
}

const OrderEntry = (props: TOrderEntryProps): React.ReactElement => {
  const { onSuccess = lodash.noop, setForm: setExternalForm = lodash.noop } =
    props

  // TODO: check settings if valid
  const {
    id,
    preset,
    requestSubmit,
    currentCustomer,
    initialValues,
    currentPrinter,
    scrollToTop = lodash.noop,
  } = useOrderEntry()

  const [internalForm, setInternalForm] = useState<FormApi>()

  const [hasMounted, setHasMounted] = useState(false)
  const [error, setError] = useState()

  const { products: pickupProducts, setProducts: setPickupProducts } =
    useProducts('pickup')
  const { products: deliveryProducts, setProducts: setDeliveryProducts } =
    useProducts('delivery')

  useEffect(() => {
    // console.info(
    //   "%cOrderEntry mount",
    //   "font-weight: bold; color: orange;",
    //   formKey
    // )

    return () => {
      setPickupProducts([])
      setDeliveryProducts([])
    }
  }, [])

  const [isLoading, setIsLoading] = useState(false)

  const setForm = (form): void => {
    setInternalForm(form)
    setExternalForm(form)
  }

  const [showConfirmation, setShowConfirmation] = useState(false)

  const { skipConfirmation } = preset

  // scroll to top when going to confirmation page
  const previousShowConfirmation = usePrevious(showConfirmation)
  useEffect(() => {
    if (
      (!previousShowConfirmation && showConfirmation) ||
      (previousShowConfirmation && !showConfirmation)
    ) {
      scrollToTop()
    }
  }, [showConfirmation])

  const showSubmitValues = async (): Promise<void> => {
    try {
      const values = lodash.get(internalForm?.getState(), 'values')

      const formatted = await formatOrderEntry(
        values as TOrderEntryFormValues,
        {
          pickupProducts,
          deliveryProducts,
          preset,
          currentPrinter,
        },
      )
      console.info({ values, formatted })
    } catch (err) {
      console.error('Failed to format values', err)
    }
  }

  const cancelPreview = (): void => {
    setShowConfirmation(false)
  }

  const onSubmit = async (values): Promise<void> => {
    try {
      setError(undefined)
      if (showConfirmation || skipConfirmation) {
        const payload = await formatOrderEntry(values, {
          pickupProducts,
          deliveryProducts,
          preset,
          currentPrinter,
        })
        //console.log('formatted submit payload', payload)
        // onSuccess({
        //   order: payload,
        //   labelToken:
        //     "eyJpdiI6IkxVMVpMM2gxL1JaNVp4bXdaRTFkNFE9PSIsInZhbHVlIjoiOUd3azkrN2F1dHVSUUd3emJLZlFvcDhDZjQ2NDVRandaTmJrUElYSnpzV3dqd2FDWFNqR0xTYUlOK2ZwZklpNGhMbnNOaG55b050WVY2azkvTUNZNkE9PSIsIm1hYyI6IjczMmU4ZmEwZjY2Mjg3YjM5NzAwODQ1YzNhNjU0YWVhZGE3ZTk4YThiOGNmYTlmM2I3YTJlOTk0ZjY1ZjFhMDEifQ=="
        // })
        // return
        // // FIXME: do the actual request
        if (!payload) {
          console.warn('OrderEntry unable to submit empty payload')
        } else {
          try {
            setIsLoading(true)
            const result = await requestSubmit({
              ...payload,
              customerId: lodash.get(currentCustomer, 'id'),
            })
            // console.log("submit successfull", result)
            onSuccess({ order: payload, labelToken: result })
          } catch (err) {
            console.error('Submit failed', err.response)

            // FIXME: workaround order created but StoreOrderService not properly functioning
            // const { data: { line, message } = {} } = err.response || {}
            // if (message === "Undefined property: stdClass::$id" && line === 96) {
            //   onSuccess(payload)
            //   return
            // }
            // TODO: set error
            setError(lodash.get(err, 'response'))
            cancelPreview()
            toast.error(
              i18n.t(
                'order_submit_failed',
                'Auftrag konnte nicht übermittelt werden.',
              ),
            )
          } finally {
            setIsLoading(false)
          }
        }
      } else {
        setShowConfirmation(true)
      }
    } catch (err) {
      console.error('Unexpected failure to submit order', err)
    }
  }

  // hack required to wait for product reset for ProductHandler (WHY?)
  useEffect(() => {
    setTimeout(() => setHasMounted(true), 0)
  }, [])

  // only render in the second tick
  if (!hasMounted) {
    return <></>
  }
  return (
    <>
      <FormComponent
        name={id}
        setForm={setForm}
        onSubmit={onSubmit}
        initialValues={initialValues}
        // showFormStateWindow={/localhost/.test(window.location)}
        // formStateWindowProps={{ expanded: false }}
        showFormStateWindowCheckbox={/localhost/.test(window.location.href)}
        subscription={{
          submitting: true,
          pristine: true,
          error: true,
        }}
        enableFocusOnErrors
        onlyMountWhenValuesAreSet
      >
        <>
          <form name={id}>
            <LoaderOverlay isLoading={isLoading} fullscreen />
            <Error error={error} setError={setError} />
            {/(localhost)/.test(window.location.hostname) && (
              <div
                className="d-flex my-2 flex-column"
                style={{
                  left: 0,
                  zIndex: 9,
                  top: '150px',
                  position: 'fixed',
                  background: `repeating-linear-gradient(
                        135deg,
                        rgba(255, 0, 0, 0.1),
                        rgba(255, 0, 0, 0.1) 10px,
                        rgba(255, 0, 0, 0.2) 10px,
                        rgba(255, 0, 0, 0.2) 20px
                      )`,
                  padding: '15px',
                }}
              >
                {/* <h3 className="text-danger">DebugControls</h3> */}
                <div className="my-1">
                  <ShowSubmitValues onClick={showSubmitValues} />
                </div>
                {/* <div className="my-1">
                  <PrefillAddresses />
                </div> */}
              </div>
            )}
            {showConfirmation ? (
              <Confirmation onCancel={cancelPreview} />
            ) : (
              <>
                <Form />
              </>
            )}
          </form>
        </>
      </FormComponent>
    </>
  )
}

export default OrderEntry
